import { supabase } from "@/integrations/supabase/client";

/**
 * Fetch all rows from a Supabase query, paginating past the server's max-rows cap.
 *
 * Supabase/PostgREST enforces a server-side row limit (db-max-rows, default 1000)
 * that overrides any client-side .limit() value. To fetch ALL rows matching a filter,
 * we must paginate with .range().
 *
 * Pass a factory function that builds a fresh query each call — Supabase query
 * builders are consumed when awaited, so we can't reuse one across pages.
 *
 * Usage:
 *   const rows = await fetchAllPages<StoreProduct>(() =>
 *     supabase.from("store_products").select("*").in("store_id", storeIds)
 *   );
 *
 * For consistent pagination, the query SHOULD include an .order() clause — without
 * one, Postgres may return rows in different orders between pages, producing
 * duplicates or gaps. The helper does not enforce this; it's the caller's job.
 */
export async function fetchAllPages<T = any>(
  buildQuery: () => any,
  pageSize: number = 1000,
): Promise<T[]> {
  const all: T[] = [];
  let from = 0;
  while (true) {
    const { data, error } = await buildQuery().range(from, from + pageSize - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    all.push(...(data as T[]));
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return all;
}
